import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { loadCouponCreateV2Request, selectCouponAmountErrorTypeV2, selectIsCouponCreateDisabledV2, selectIsCouponQuickBetsV2, selectUserIsAuthenticated, useQueryPopupOpen } from '@mwl/core-lib';
import type { ButtonBaseProps } from '@/components/Button/Button';
import { Button } from '@/components/Button/Button';
import { useTypedDispatch, useTypedSelector } from '@/hooks';
import { selectCouponPreviewState, setShowMobileCoupon, setShowPreviewCoupon } from '@/store';
import type { CouponSubmitProps } from './CouponSubmit.types';
import styles from './CouponSubmit.module.scss';
const commonButtonProps: ButtonBaseProps = {
  variant: 'filled',
  color: 'red',
  size: 'lg',
  fontWeight: 'bold',
  align: 'center',
  fontSize: 'lg',
  rounded: true,
  fullWidth: true
};
const CouponSubmit: FC<CouponSubmitProps> = ({
  disabled,
  classes
}) => {
  const {
    t
  } = useTranslation('sport');
  const dispatch = useTypedDispatch();
  const {
    onOpenPopup,
    createLinkProps
  } = useQueryPopupOpen();
  const signupLinkProps = createLinkProps({
    queryName: 'register'
  });
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const isShowPreviewCoupon = useTypedSelector(selectCouponPreviewState);
  const isQuickBets = useTypedSelector(selectIsCouponQuickBetsV2);
  const isCouponCreateDisabled = useTypedSelector(selectIsCouponCreateDisabledV2);
  const amountErrorType = useTypedSelector(selectCouponAmountErrorTypeV2);
  const isDisableCreate = disabled || isCouponCreateDisabled;
  const showMobileCoupon = useCallback(() => {
    dispatch(setShowPreviewCoupon(false));
    dispatch(setShowMobileCoupon(true));
  }, [dispatch]);
  const createCoupon = useCallback(() => {
    dispatch(loadCouponCreateV2Request());
  }, [dispatch]);
  const showDepositPopup = useCallback(() => {
    onOpenPopup({
      queryName: 'deposit'
    });
  }, [onOpenPopup]);
  const buttonContent = useMemo(() => {
    if (!isAuth) {
      return <Button as="link" {...signupLinkProps} {...commonButtonProps}>
          {t('common:header.account.sign_up', 'Sign Up')}
        </Button>;
    }
    if (isQuickBets) {
      return null;
    }
    if (amountErrorType === 'NOT_ENOUGH_BALANCE') {
      return <Button as="button" onClick={showDepositPopup} {...commonButtonProps}>
          {t('coupon.account_deposit', 'Deposit')}
        </Button>;
    }
    return isShowPreviewCoupon ? <Button as="button" onClick={showMobileCoupon} {...commonButtonProps} className={classes?.button}>
        {t('coupon.to_bets', 'To bets')}
      </Button> : <Button as="button" {...commonButtonProps} disabled={isDisableCreate} onClick={createCoupon} className={classes?.button}>
        {t('coupon.place_bet', 'Place a bet')}
      </Button>;
  }, [isAuth, isQuickBets, isShowPreviewCoupon, isDisableCreate, showMobileCoupon, classes?.button, t, createCoupon, amountErrorType, showDepositPopup, signupLinkProps]);
  return buttonContent ? <div className={styles.root} data-sentry-component="CouponSubmit" data-sentry-source-file="CouponSubmit.tsx">{buttonContent}</div> : null;
};
export { CouponSubmit };